import React, { useState, useEffect } from 'react';
import './App.css';
import Loader from './components/Loader';
import { useScrollReveal } from './hooks/useScrollReveal';
import img1 from './assets/Gallery/image1.png';
import img2 from './assets/Gallery/image2.png';
import img3 from './assets/Gallery/image3.png';
import img4 from './assets/Gallery/image4.png';

import { 
  FileCheck, 
  AlertTriangle, 
  Users, 
  TrendingUp, 
  CheckCircle, 
  ClipboardList,
  Award,
  QrCode,
  Building2,
  Ship,
  Hotel,
  Factory,
  HardHat,
  Shield,
  DollarSign,
  Mail,
  Phone,
  MapPin,
  ChevronDown,
  Send,
  Instagram,
  Facebook,
  Linkedin,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
} from 'lucide-react';

// Header Component
const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToEnquiry = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className={`header ${scrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          <div className="logo">
            <img src="/LCP_Logo.png" alt="LCP" className="logo-img" />
            <div className="logo-text">
              <span className="logo-title">Labour Certification Program</span>
              <span className="logo-subtitle">AIMRI Industrial Varsity</span>
            </div>
          </div>
          <button className="btn-header" onClick={scrollToEnquiry}>
            Get Started
          </button>
        </div>
      </div>
    </header>
  );
};

// Hero Section with Industrial Image
const Hero = () => {
  const scrollToEnquiry = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero hero-with-image">
      <div className="hero-overlay"></div>
      <div className="container">
        <div className="hero-content">
          <h1 className="hero-title">
            On-site skill verification<br />for industrial workforces
          </h1>
          <p className="hero-subtitle">
            Reduce rework, improve safety, and retain skilled workers through<br />employer-validated certification
          </p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={scrollToEnquiry}>
              Request Briefing
            </button>
            <a 
              href="/employer-explainer.pdf" 
              className="btn-secondary"
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Brochure
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Problem Section
const Problem = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  
  const problems = [
    { icon: <AlertTriangle size={28} />, text: "Skills invisible until work begins" },
    { icon: <TrendingUp size={28} />, text: "Unpredictable quality across workers" },
    { icon: <FileCheck size={28} />, text: "Rework increases project costs" }
  ];

  return (
    <section 
      ref={sectionRef} 
      className={`section problem-section scroll-reveal ${isVisible ? 'visible' : ''}`}
    >
      <div className="container">
        <h2 className="section-title">The Employer Challenge</h2>
        <div className="problem-grid">
          {problems.map((problem, index) => (
            <div key={index} className="problem-card">
              <div className="problem-icon">{problem.icon}</div>
              <p className="problem-text">{problem.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution Section
const Solution = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  
  return (
    <section 
      ref={sectionRef} 
      className={`section solution-section scroll-reveal ${isVisible ? 'visible' : ''}`}
    >
      <div className="container">
        <div className="solution-content">
          <div className="solution-text">
            <h2 className="section-title left">What is LCP</h2>
            <p className="solution-desc">
              A structured on-site skill verification system that enables employers to validate 
              worker competency through direct observation and recommendation.
            </p>
            <ul className="solution-list">
              <li><CheckCircle size={20} /> Employer-recommended certification</li>
              <li><CheckCircle size={20} /> Industry-specific skill validation</li>
              <li><CheckCircle size={20} /> Career pathway foundation</li>
              <li><CheckCircle size={20} /> No academic prerequisites</li>
            </ul>
          </div>
          <div className="solution-visual">
            <div className="visual-card visual-card-with-image">
              <Award size={48} className="visual-icon" />
              <h3>Verified Skills</h3>
              <p>On-site competency validation</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works with Process Image
const HowItWorks = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  
  const steps = [
    { title: "On-Site Work", desc: "Worker performs job tasks", icon: <HardHat size={24} /> },
    { title: "Skill Verification", desc: "Supervisor validates skills", icon: <ClipboardList size={24} /> },
    { title: "Employer Endorsement", desc: "Company recommends certification", icon: <FileCheck size={24} /> },
    { title: "Digital Certificate", desc: "QR-verified credential issued", icon: <QrCode size={24} /> }
  ];

  return (
    <section 
      ref={sectionRef} 
      className={`section how-section how-section-with-bg scroll-reveal ${isVisible ? 'visible' : ''}`}
    >
      <div className="how-section-overlay"></div>
      <div className="container">
        <h2 className="section-title">How LCP Works</h2>
        <div className="steps-timeline">
          {steps.map((step, index) => (
            <div key={index} className="step-item">
              <div className="step-number">{index + 1}</div>
              <div className="step-icon">{step.icon}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Combined Gallery & Skill Levels Section
const GalleryAndSkills = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const images = [
    { src: img1, alt: 'LCP Certification Process', title: 'Certification Process' },
    { src: img2, alt: 'Skills Assessment', title: 'Skills Assessment' },
    { src: img3, alt: 'Industrial Training', title: 'Industrial Training' },
    { src: img4, alt: 'Worker Development', title: 'Worker Development' }
  ];

  const levels = [
    { level: "Level 1", title: "Helper", desc: "Basic tasks under supervision" },
    { level: "Level 2", title: "Skilled Worker", desc: "Independent execution" },
    { level: "Level 3", title: "Advanced", desc: "Complex task capability" },
    { level: "Level 4", title: "Specialist", desc: "Expert-level execution" }
  ];

  // Auto-slide effect
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, images.length]);

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section 
      ref={sectionRef} 
      className={`section gallery-skills-section scroll-reveal ${isVisible ? 'visible' : ''}`}
    >
      <div className="container">
        <div className="gallery-skills-grid">
          {/* Gallery Section */}
          <div className="gallery-column">
            <div className="gallery-header">
              <ImageIcon size={28} className="gallery-header-icon" />
              <h2 className="section-title left">See LCP in Action</h2>
              <p className="section-subtitle left">Real-world certification and skill development</p>
            </div>
            
            <div 
              className="carousel-container"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="carousel-wrapper">
                <div 
                  className="carousel-track"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {images.map((image, index) => (
                    <div key={index} className="carousel-slide">
                      <img src={image.src} alt={image.alt} />
                      <div className="carousel-caption">
                        <h3>{image.title}</h3>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation Arrows */}
                <button 
                  className="carousel-nav carousel-prev" 
                  onClick={goToPrevious}
                  aria-label="Previous slide"
                >
                  <ChevronLeft size={28} />
                </button>
                
                <button 
                  className="carousel-nav carousel-next" 
                  onClick={goToNext}
                  aria-label="Next slide"
                >
                  <ChevronRight size={28} />
                </button>
              </div>

              {/* Dots Navigation */}
              <div className="carousel-dots">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${currentSlide === index ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              {/* Counter */}
              <div className="carousel-counter">
                {currentSlide + 1} / {images.length}
              </div>
            </div>
          </div>

          {/* Skill Levels Section */}
          <div className="skills-column">
            <div className="skills-header">
              <Award size={28} className="skills-header-icon" />
              <h2 className="section-title left">Skill Levels</h2>
              <p className="section-subtitle left">Progressive certification pathway</p>
            </div>
            
            <div className="levels-stack">
              {levels.map((level, index) => (
                <div key={index} className="level-card-compact">
                  <div className="level-card-header">
                    <span className="level-badge">{level.level}</span>
                    <h3 className="level-title">{level.title}</h3>
                  </div>
                  <p className="level-desc">{level.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Benefits with Accordion
const Benefits = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  const [activeIndex, setActiveIndex] = useState(0);

  const categories = [
    {
      title: "Quality & Operations",
      icon: <FileCheck size={24} />,
      benefits: [
        "Reduce rework and corrections",
        "Deploy workers with verified skills",
        "Baseline skill visibility before assignment",
        "Standardize quality expectations"
      ]
    },
    {
      title: "Safety & Risk",
      icon: <Shield size={24} />,
      benefits: [
        "Document worker competency",
        "Reduce liability in skill-critical tasks",
        "Support compliance audits",
        "Minimize operational incidents"
      ]
    },
    {
      title: "HR & Retention",
      icon: <Users size={24} />,
      benefits: [
        "Retain workers through career pathway",
        "Recognize skill without degree barriers",
        "Reduce supervisor micromanagement time",
        "Improve worker motivation"
      ]
    },
    {
      title: "Cost & Brand",
      icon: <DollarSign size={24} />,
      benefits: [
        "Lower per-worker certification cost",
        "Strengthen employer brand",
        "Support ESG commitments",
        "Differentiate from competitors"
      ]
    }
  ];

  return (
    <section 
      ref={sectionRef} 
      className={`section benefits-section scroll-reveal ${isVisible ? 'visible' : ''}`}
    >
      <div className="container">
        <h2 className="section-title">Employer Benefits</h2>
        <div className="benefits-accordion">
          {categories.map((category, index) => (
            <div key={index} className={`accordion-item ${activeIndex === index ? 'active' : ''}`}>
              <button 
                className="accordion-header"
                onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}
              >
                <div className="accordion-title-wrapper">
                  <span className="accordion-icon">{category.icon}</span>
                  <span className="accordion-title">{category.title}</span>
                </div>
                <ChevronDown className={`accordion-chevron ${activeIndex === index ? 'rotate' : ''}`} size={20} />
              </button>
              <div className={`accordion-content ${activeIndex === index ? 'show' : ''}`}>
                <ul className="benefits-list">
                  {category.benefits.map((benefit, idx) => (
                    <li key={idx}>{benefit}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Cost Comparison
const Cost = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  
  return (
    <section 
      ref={sectionRef} 
      className={`section cost-section scroll-reveal ${isVisible ? 'visible' : ''}`}
    >
      <div className="container">
        <h2 className="section-title">Investment Logic</h2>
        <p className="section-subtitle">Per worker certification cost versus operational expenses</p>
        <div className="cost-grid">
          <div className="cost-card">
            <span className="cost-amount">₹1,000</span>
            <p className="cost-label">Less than 1 day rework</p>
          </div>
          <div className="cost-card">
            <span className="cost-amount">₹1,000</span>
            <p className="cost-label">Less than 1 HR interview</p>
          </div>
          <div className="cost-card">
            <span className="cost-amount">₹1,000</span>
            <p className="cost-label">Less than 1 safety incident</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Industries with Visual Cards
const Industries = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  const [activeIndustry, setActiveIndustry] = useState(null);

  const industries = [
    { 
      icon: <Building2 size={32} />, 
      name: "Construction & EPC", 
      image: "construction",
      description: "Verify skills for welders, fitters, riggers, and site supervisors. Reduce rework on critical infrastructure projects through employer-validated competency."
    },
    { 
      icon: <Ship size={32} />, 
      name: "Shipyards", 
      image: "shipyard",
      description: "Document marine fabrication skills, hull welding, and piping expertise. Meet maritime quality standards with certified workforces."
    },
    { 
      icon: <Factory size={32} />, 
      name: "Industrial Plants", 
      image: "factory",
      description: "Validate process technicians, maintenance crews, and equipment operators. Support operational safety and quality compliance."
    },
    { 
      icon: <Building2 size={32} />, 
      name: "Facility Management", 
      image: "facility",
      description: "Certify MEP technicians, HVAC specialists, and building maintenance teams. Standardize service quality across portfolios."
    },
    { 
      icon: <Hotel size={32} />, 
      name: "Hospitality", 
      image: "hospitality",
      description: "Verify culinary skills, housekeeping expertise, and service proficiency. Elevate guest experience through certified talent."
    },
    { 
      icon: <Building2 size={32} />, 
      name: "Healthcare", 
      image: "healthcare",
      description: "Document support staff competencies, biomedical technicians, and facility operations. Support compliance and patient safety."
    }
  ];

  const handleCardClick = (index) => {
    setActiveIndustry(activeIndustry === index ? null : index);
  };

  const scrollToEnquiry = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      ref={sectionRef} 
      className={`section industries-section industries-with-images scroll-reveal ${isVisible ? 'visible' : ''}`}
    >
      <div className="container">
        <h2 className="section-title">Industries We Serve</h2>
        <div className="industries-grid">
          {industries.map((industry, index) => (
            <div key={index}>
              <div 
                className={`industry-card industry-${industry.image} ${activeIndustry === index ? 'active' : ''}`}
                onClick={() => handleCardClick(index)}
              >
                <div className="industry-overlay"></div>
                <div className="industry-content">
                  <div className="industry-icon">{industry.icon}</div>
                  <span className="industry-name">{industry.name}</span>
                </div>
              </div>
              
              {activeIndustry === index && (
                <div className="industry-detail">
                  <p className="industry-description">{industry.description}</p>
                  <button className="btn-industry-cta" onClick={scrollToEnquiry}>
                    Request Industry Briefing <ArrowRight size={16} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Career Pathway with Background
const Pathway = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  
  const steps = [
    { stage: "Certified Skill", desc: "On-site competency verified" },
    { stage: "Diploma-on-Experience", desc: "Skill credit toward qualification" },
    { stage: "Degree Program", desc: "Academic pathway opened" },
    { stage: "Advanced Specialist", desc: "Career progression enabled" }
  ];

  return (
    <section 
      ref={sectionRef} 
      className={`section pathway-section pathway-with-bg scroll-reveal ${isVisible ? 'visible' : ''}`}
    >
      <div className="pathway-overlay"></div>
      <div className="container">
        <h2 className="section-title">Career Pathway</h2>
        <p className="section-subtitle">Skill recognized today → Career pathway tomorrow</p>
        <div className="pathway-timeline">
          {steps.map((step, index) => (
            <div key={index} className="pathway-item">
              <div className="pathway-number">{index + 1}</div>
              <div className="pathway-line"></div>
              <div className="pathway-content">
                <h3 className="pathway-stage">{step.stage}</h3>
                <p className="pathway-desc">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form
const Contact = () => {
  const [sectionRef, isVisible] = useScrollReveal();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formsubmit.co/ajax/LCP@aimri.in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: `LCP Enquiry - ${formData.company}`,
          _template: 'table',
          _captcha: 'false'
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', company: '', industry: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      }
    } catch (error) {
      console.error('Form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={sectionRef} 
      className={`section contact-section contact-with-cta-bg scroll-reveal ${isVisible ? 'visible' : ''}`} 
      id="contact"
    >
      <div className="contact-cta-overlay"></div>
      <div className="container">
        <div className="contact-grid">
          <div className="contact-info">
            <h2 className="contact-title">Get in Touch</h2>
            <p className="contact-text">
              Connect with our team to discuss implementation and pilot programs.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <Mail size={20} />
                <a href="mailto:LCP@aimri.in">LCP@aimri.in</a>
              </div>
              <div className="contact-item">
                <Phone size={20} />
                <a href="tel:+918714408777">+91 87144 08777</a>
              </div>
              <div className="contact-item">
                <MapPin size={20} />
                <span>AIMRI Industrial Varsity</span>
              </div>
            </div>
            <div className="contact-social">
              <a href="https://www.instagram.com/labourcertificationprogram/" target="_blank" rel="noopener noreferrer" className="social-link">
                <Instagram size={20} />
              </a>
              <a href="https://www.facebook.com/share/1FnYZ1c1Tp/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="social-link">
                <Facebook size={20} />
              </a>
              <a href="https://www.linkedin.com/company/labour-certification-program/" target="_blank" rel="noopener noreferrer" className="social-link">
                <Linkedin size={20} />
              </a>
            </div>
            <a 
              href="https://wa.me/message/HBQAF6PC7EZDO1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="whatsapp-cta"
            >
              Chat on WhatsApp <ArrowRight size={18} />
            </a>
          </div>
          
          <form className="contact-form" onSubmit={handleSubmit}>
            {submitted && (
              <div className="form-success">
                <CheckCircle size={20} />
                <span>Thank you! We'll be in touch shortly.</span>
              </div>
            )}
            
            <div className="form-row">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full Name *"
                required
                disabled={isSubmitting}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email Address *"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form-row">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone Number *"
                required
                disabled={isSubmitting}
              />
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Company Name *"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            >
              <option value="">Select Industry *</option>
              <option value="Construction & EPC">Construction & EPC</option>
              <option value="Shipyards">Shipyards</option>
              <option value="Industrial Plants">Industrial Plants</option>
              <option value="Facility Management">Facility Management</option>
              <option value="Hotels & Hospitality">Hotels & Hospitality</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Other">Other</option>
            </select>
            
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Your message..."
              rows="4"
              disabled={isSubmitting}
            ></textarea>
            
            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Enquiry
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-col">
            <h4>Labour Certification Program</h4>
            <p>AIMRI Industrial Varsity</p>
            <p>Employer-facing skill verification</p>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <p>LCP@aimri.in</p>
            <p>+91 87144 08777</p>
          </div>
          <div className="footer-col">
            <h4>Follow Us</h4>
            <div className="footer-social">
              <a href="https://www.instagram.com/labourcertificationprogram/" target="_blank" rel="noopener noreferrer">
                <Instagram size={18} />
              </a>
              <a href="https://www.facebook.com/share/1FnYZ1c1Tp/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">
                <Facebook size={18} />
              </a>
              <a href="https://www.linkedin.com/company/labour-certification-program/" target="_blank" rel="noopener noreferrer">
                <Linkedin size={18} />
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 AIMRI Industrial Varsity. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  const [loading, setLoading] = useState(true);

  const handleLoadComplete = () => {
    setLoading(false);
  };

  if (loading) {
    return <Loader onLoadComplete={handleLoadComplete} />;
  }

  return (
    <div className="App app-fade-in">
      <Header />
      <Hero />
      <Problem />
      <Solution />
      <HowItWorks />
      <GalleryAndSkills />
      <Benefits />
      <Cost />
      <Industries />
      <Pathway />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;